/**
 * SettingsPanel — auto-renders host controls from LOBBY_SETTING_DEFS.
 * Drawer-style on mobile (bottom sheet), centered modal on wider screens.
 * Hidden for non-hosts.
 *
 * Per docs/ui/room-screens.md §4.8 — settings groups now render with
 * vellum chapter dividers (Game / Pacing / Social / Scoring) so the
 * panel reads as the keeper's ledger instead of one long flat list.
 */
import { useEffect, useState, Fragment } from 'react';
import { LOBBY_SETTING_DEFS, visibleFields, type LobbySettings } from '@tt/lobby';
import type { GameTile } from '../../lib/api';
import { Gamepad2, Clock, Users, Trophy } from 'lucide-react';
import { gameAvailability, playerReqLabel } from './gameAvailability';
import { useEscapeClose } from '../../lib/useEscapeClose';

const GROUP_LABELS: Record<string, string> = {
  game: 'Game',
  pacing: 'Pacing',
  social: 'Social',
  scoring: 'Scoring',
};
const GROUP_ICONS: Record<string, React.ReactNode> = {
  game: <Gamepad2 size={14} />,
  pacing: <Clock size={14} />,
  social: <Users size={14} />,
  scoring: <Trophy size={14} />,
};

interface Props {
  open: boolean;
  isHost: boolean;
  settings: LobbySettings;
  games: GameTile[];
  playerCount?: number;
  onClose: () => void;
  onChange(patch: Record<string, unknown>): void;
}

export function SettingsPanel({ open, isHost, settings, games, playerCount, onClose, onChange }: Props) {
  const [draft, setDraft] = useState<LobbySettings>(settings);
  useEffect(() => { setDraft(settings); }, [settings]);
  useEscapeClose(open, onClose);

  function update<K extends keyof LobbySettings>(key: K, value: LobbySettings[K]) {
    const next = { ...draft, [key]: value };
    setDraft(next);
    onChange({ [key]: value });
  }

  if (!open) return null;
  return (
    <div className="settings-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="settings-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="settings-header">
          <h2 className="display">Lobby settings</h2>
          <button type="button" className="link" onClick={onClose}>close</button>
        </header>
        {!isHost && (
          <p className="muted">Only the host can change settings.</p>
        )}
        <div className="settings-body">
          {visibleFields(draft).map(({ key, meta }, idx, arr) => {
            const def = LOBBY_SETTING_DEFS[key];
            const value = draft[key] as unknown;
            const id = `set-${key}`;
            const prev = idx > 0 ? arr[idx - 1] : null;
            const showDivider = !prev || prev.meta.group !== meta.group;
            return (
              <Fragment key={key}>
                {showDivider && (
                  <div className="settings-chapter" data-group={meta.group}>
                    <span className="settings-chapter-icon" aria-hidden="true">
                      {GROUP_ICONS[meta.group ?? 'game']}
                    </span>
                    <span className="settings-chapter-label">
                      {GROUP_LABELS[meta.group ?? 'game'] ?? meta.group}
                    </span>
                    <span className="settings-chapter-rule" aria-hidden="true" />
                  </div>
                )}
              <div className="setting-row" data-group={meta.group}>
                <label htmlFor={id} className="setting-label">
                  <span>{meta.label}</span>
                  {meta.hint && <span className="setting-hint muted">{meta.hint}</span>}
                </label>
                {meta.control === 'toggle' ? (
                  <input
                    id={id}
                    type="checkbox"
                    checked={!!value}
                    disabled={!isHost}
                    onChange={(e) => update(key, e.target.checked as LobbySettings[typeof key])}
                  />
                ) : meta.control === 'number' ? (
                  <NumberSetting
                    id={id}
                    initial={Number(value)}
                    disabled={!isHost}
                    onCommit={(n) => update(key, n as LobbySettings[typeof key])}
                  />
                ) : meta.control === 'select' ? (
                  <select
                    id={id}
                    value={String(value)}
                    disabled={!isHost}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const opts = (def.schema as unknown as { options?: unknown[] }).options ?? [];
                      const v = opts.includes(raw) ? raw : Number(raw);
                      update(key, v as LobbySettings[typeof key]);
                    }}
                    className="setting-input"
                  >
                    {extractEnumOptions(def.schema).map((opt) => (
                      <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
                    ))}
                  </select>
                ) : meta.control === 'multi-select' && meta.optionsFromRegistry === 'games' ? (
                  <div className="setting-chips">
                    {games.map((g) => {
                      const selected = (value as string[]).includes(g.id);
                      const empty = (value as string[]).length === 0;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          className={`chip${selected || empty ? ' on' : ''}`}
                          disabled={!isHost}
                          onClick={() => {
                            const cur = new Set(value as string[]);
                            if (cur.has(g.id)) cur.delete(g.id);
                            else cur.add(g.id);
                            update(key, Array.from(cur) as LobbySettings[typeof key]);
                          }}
                        >
                          {g.name}
                          <span className="chip-subtle">
                            {playerCount !== undefined && !gameAvailability(g, playerCount).playable
                              ? gameAvailability(g, playerCount).reason
                              : playerReqLabel(g)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Local-buffered number input. Tracks the typed string so a half-typed
 * value (empty / "1" mid-typing-15) doesn't flush a 0 to the server
 * and trip the bad-settings range check. Commits on blur or Enter.
 */
function NumberSetting({ id, initial, disabled, onCommit }: {
  id: string;
  initial: number;
  disabled: boolean;
  onCommit: (n: number) => void;
}) {
  const [text, setText] = useState<string>(String(initial));
  // If the upstream value changes (other host edited), reflect it.
  useEffect(() => { setText(String(initial)); }, [initial]);
  function commit() {
    const n = Number(text);
    if (Number.isFinite(n) && Number.isInteger(n) && n !== initial) onCommit(n);
    else setText(String(initial));
  }
  return (
    <input
      id={id}
      type="number"
      value={text}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      className="setting-input"
    />
  );
}

function extractEnumOptions(schema: unknown): unknown[] {
  // Zod 4 stores enum members at .options for ZodEnum, .options for ZodUnion of literals.
  const s = schema as { options?: unknown[]; def?: { values?: unknown[]; entries?: Record<string, unknown> } };
  if (s.options && Array.isArray(s.options)) {
    return s.options.map((o) => {
      const lit = (o as { value?: unknown; def?: { value?: unknown } }).value
        ?? (o as { def?: { value?: unknown } }).def?.value;
      return lit !== undefined ? lit : o;
    });
  }
  if (s.def?.entries) return Object.values(s.def.entries);
  if (s.def?.values) return s.def.values;
  return [];
}
